import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  InputNumber, 
  Input, 
  Space, 
  Popconfirm, 
  message, 
  Select,
  Typography,
  Tag,
  DatePicker 
} from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined, EditOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { loadUnloadFeeApi, vehicleCapacityTonsApi } from '../services/api';
import { LoadUnloadFee, VehicleCapacityTons } from '../types';
import { inputNumberFormatter, inputNumberParser } from '../utils/numberFormatter';
import { useLanguage } from '../contexts/LanguageContext';

const { Title } = Typography;
const { Option } = Select;

const LoadUnloadFeePage: React.FC = () => {
  const { t } = useLanguage();
  const [loadUnloadFees, setLoadUnloadFees] = useState<LoadUnloadFee[]>([]);
  const [capacityTons, setCapacityTons] = useState<VehicleCapacityTons[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<LoadUnloadFee | null>(null);
  const [selectedHistory, setSelectedHistory] = useState<any[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [loadUnloadFeesRes, capacityRes] = await Promise.all([
        loadUnloadFeeApi.getAll(),
        vehicleCapacityTonsApi.getAll()
      ]);
      
      setLoadUnloadFees(loadUnloadFeesRes.data);
      setCapacityTons(capacityRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to fetch load/unload fee data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({
      affectedDate: dayjs()
    });
    setModalVisible(true);
  };

  const handleEdit = (item: LoadUnloadFee) => {
    setEditingItem(item);
    form.setFieldsValue({
      fee: item.fee,
      notes: item.notes,
      affectedDate: dayjs(item.affectedDate)
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await loadUnloadFeeApi.delete(id);
      message.success('Load/unload fee deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting load/unload fee:', error);
      message.error('Failed to delete load/unload fee');
    }
  };

  const handleViewHistory = async (item: LoadUnloadFee) => {
    try {
      const response = await loadUnloadFeeApi.getHistory(item.id);
      setSelectedHistory(response.data);
      setHistoryModalVisible(true);
    } catch (error) {
      console.error('Error fetching history:', error);
      message.error('Failed to fetch load/unload fee history');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const formattedValues = {
        ...values,
        affectedDate: values.affectedDate ? values.affectedDate.format('YYYY-MM-DD') : undefined
      };
      
      if (editingItem) {
        await loadUnloadFeeApi.update(editingItem.id, formattedValues);
        message.success('Load/unload fee updated successfully');
      } else {
        await loadUnloadFeeApi.create(formattedValues);
        message.success('Load/unload fee created successfully');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error: any) {
      console.error(`Error ${editingItem ? 'updating' : 'creating'} load/unload fee:`, error);
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      message.error(`Error ${editingItem ? 'updating' : 'creating'} load/unload fee: ${errorMessage}`);
    }
  };

  const columns: ColumnsType<LoadUnloadFee> = [
    {
      title: t('vehicle.capacity'),
      dataIndex: 'capacityTons',
      key: 'capacityTons',
      sorter: (a, b) => a.capacityTons - b.capacityTons,
      render: (value) => `${value} tons`
    },
    {
      title: t('load.unload.fee'),
      dataIndex: 'fee',
      key: 'fee',
      sorter: (a, b) => a.fee - b.fee,
      render: (value) => new Intl.NumberFormat('vi-VN', { 
        style: 'currency', 
        currency: 'VND' 
      }).format(value)
    },
    {
      title: t('affected.date'),
      dataIndex: 'affectedDate',
      key: 'affectedDate',
      sorter: (a: LoadUnloadFee, b: LoadUnloadFee) => new Date(a.affectedDate).getTime() - new Date(b.affectedDate).getTime(),
      render: (value: string) => new Date(value).toLocaleDateString()
    },
    {
      title: t('notes'),
      dataIndex: 'notes',
      key: 'notes',
      render: (value) => value || '-'
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('active') : t('inactive')}
        </Tag>
      ),
      filters: [
        { text: t('active'), value: true },
        { text: t('inactive'), value: false }
      ],
      onFilter: (value, record) => record.isActive === value
    },
    {
      title: t('created.by'),
      dataIndex: 'createdByUserName',
      key: 'createdByUserName'
    },
    {
      title: t('created.at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<HistoryOutlined />}
            onClick={() => handleViewHistory(record)}
            size="small"
          />
          <Popconfirm
            title={t('delete.load.unload.fee.confirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('yes')}
            cancelText={t('no')}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const historyColumns = [
    {
      title: t('action'),
      dataIndex: 'action',
      key: 'action'
    },
    {
      title: t('old.price'),
      dataIndex: 'oldFee',
      key: 'oldFee',
      render: (value: number) => value ? new Intl.NumberFormat('vi-VN', { 
        style: 'currency', 
        currency: 'VND' 
      }).format(value) : '-'
    },
    {
      title: t('new.price'),
      dataIndex: 'newFee',
      key: 'newFee',
      render: (value: number) => new Intl.NumberFormat('vi-VN', { 
        style: 'currency', 
        currency: 'VND' 
      }).format(value)
    },
    {
      title: t('affected.date'),
      dataIndex: 'affectedDate',
      key: 'affectedDate',
      render: (value: string) => value ? new Date(value).toLocaleDateString() : '-'
    },
    {
      title: t('reason'),
      dataIndex: 'reason',
      key: 'reason',
      render: (value: string) => value || '-'
    },
    {
      title: t('changed.by'),
      dataIndex: 'changedByUserName',
      key: 'changedByUserName'
    },
    {
      title: t('date'),
      dataIndex: 'changeDate',
      key: 'changeDate',
      render: (value: string) => new Date(value).toLocaleString()
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>{t('load.unload.fee.management')}</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            {t('add.load.unload.fee.button')}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={loadUnloadFees}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />

        <Modal
          title={editingItem ? t('edit.load.unload.fee') : t('add.load.unload.fee')}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={600}
        >
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            onFinish={handleSubmit}
          >
            {!editingItem && (
              <Form.Item
                label={t('vehicle.capacity')}
                name="capacityTonsId"
                rules={[{ required: true, message: t('please.select') + ' ' + t('vehicle.capacity') }]}
              >
                <Select placeholder={t('please.select') + ' ' + t('vehicle.capacity')}>
                  {capacityTons.map(ct => (
                    <Option key={ct.id} value={ct.id}>
                      {ct.capacity} tons {ct.description && `(${ct.description})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              label={t('load.unload.fee.amount')}
              name="fee"
              rules={[
                { required: true, message: t('please.enter') ? t('please.enter') + ' ' + t('load.unload.fee.amount') : 'Please enter the fee amount' },
                { type: 'number', min: 0, message: t('load.unload.fee.amount') + ' >= 0' }
              ]}
            >
              <InputNumber
                min={0}
                step={1000}
                style={{ width: '100%' }}
                placeholder={t('load.unload.fee.amount')}
                formatter={inputNumberFormatter}
                parser={inputNumberParser}
              />
            </Form.Item>

            <Form.Item
              label={t('affected.date')}
              name="affectedDate"
              rules={[
                { required: true, message: t('please.select') + ' ' + t('affected.date') }
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder={t('please.select') + ' ' + t('affected.date')}
              />
            </Form.Item>

            <Form.Item
              label={t('notes')}
              name="notes"
            >
              <Input.TextArea
                rows={3}
                placeholder={t('notes')}
              />
            </Form.Item>

            {editingItem && (
              <Form.Item
                label={t('reason.for.change')}
                name="reason"
              >
                <Input.TextArea
                  rows={2}
                  placeholder={t('reason.for.change')}
                />
              </Form.Item>
            )}
          </Form>
        </Modal>

        <Modal
          title={t('load.unload.fee.history')}
          open={historyModalVisible}
          onCancel={() => setHistoryModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setHistoryModalVisible(false)}>
              {t('close')}
            </Button>
          ]}
          width={800}
        >
          <Table
            columns={historyColumns}
            dataSource={selectedHistory}
            rowKey="id"
            pagination={{ pageSize: 5 }}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default LoadUnloadFeePage;